import React, { memo, useMemo } from "react";

import { ChartContainer } from "../../core/ChartContainer";
import { PriceSeries } from "../../series/PriceSeries";
import { BaselineLine } from "../../overlays/BaselineLine";
import { AnimatedPriceSeries } from "../../series/AnimatedPriceSeries";
import {
  useChartStore,
  useChartSubscription,
  useRealtimeMarketData,
} from "~hooks/ticker-chart";
import type { ChartPadding, DataConfig, TickerSeries } from "~types/types";

export type SparkLineProps = {
  readonly series: TickerSeries;
  readonly width: number;
  readonly height: number;
  readonly showGradient?: boolean;
  readonly showMultiColorLine?: boolean;
  readonly usePreviousCloseAsBaseline?: boolean;
  readonly showBaseline?: boolean;
  readonly strokeWidth?: number;
  readonly padding?: ChartPadding;
  readonly dataConfig?: DataConfig;
  readonly accessibilityLabel?: string;
  readonly animate?: boolean;
  readonly animationDuration?: number;
  readonly enableRealTime?: boolean;
};

const SPARKLINE_PADDING: ChartPadding = {
  top: 4,
  right: 0,
  bottom: 4,
  left: 0,
};

function SparkLineInner({
  series,
  width,
  height,
  showGradient = true,
  showMultiColorLine = false,
  usePreviousCloseAsBaseline = false,
  showBaseline = false,
  strokeWidth = 1.5,
  padding,
  dataConfig,
  accessibilityLabel,
  animate = false,
  animationDuration,
  enableRealTime = false,
}: SparkLineProps) {
  const seriesArray = useMemo(() => [series], [series]);

  const mergedPadding = useMemo(
    () => ({ ...SPARKLINE_PADDING, ...padding }),
    [padding]
  );

  const store = useChartStore({
    mode: "single",
    chartType: "line",
    series: seriesArray,
    width,
    height,
    padding: mergedPadding,
    yAxisPosition: "right",
    viewportEndBuffer: false,
    dataConfig,
    usePreviousCloseAsBaseline,
  });

  useRealtimeMarketData({
    ticker: series.id,
    chartType: "line",
    enableRealTime,
    store,
  });

  const _revision = useChartSubscription(store, ["data", "scales"]);
  const layout = store.getLayout();

  const priceSeriesContent = (
    <PriceSeries
      chartType="line"
      seriesId={series.id}
      showGradient={showGradient}
      showMultiColorLine={showMultiColorLine}
      strokeWidth={strokeWidth}
    />
  );

  return (
    <ChartContainer
      ariaLabel={accessibilityLabel ?? `Sparkline for ${series.name}`}
      height={height}
      store={store}
      width={width}
    >
      <svg height={height} width={width}>
        <rect fill="transparent" height={height} width={width} x={0} y={0} />

        {!animate && priceSeriesContent}

        {showBaseline && (
          <BaselineLine
            currency={series.issuedCurrency}
            hideYAxisLabel={true}
            seriesId={series.id}
          />
        )}
      </svg>

      {animate && (
        <svg height={height} width={width}>
          <AnimatedPriceSeries
            chartType="line"
            duration={animationDuration}
            height={layout.priceArea.height}
            seriesId={series.id}
            width={layout.priceArea.width}
            x={layout.priceArea.x}
            y={layout.priceArea.y}
          >
            {priceSeriesContent}
          </AnimatedPriceSeries>
        </svg>
      )}
    </ChartContainer>
  );
}

SparkLineInner.displayName = "SparkLine";

export const SparkLine = memo(SparkLineInner);

export default SparkLine;
